import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

type SubMenuItem = string | { label: string; subMenu: string[] };
type MenuItem = {
  label: string;
  subMenu?: SubMenuItem[];
};

const menuItems: MenuItem[] = [

  {
    label: "Customer",
    subMenu: [
      "New Customer",
      //"Create Project",
      //"View Project",
      "Payment History",
      "Documents Sent",
    ],
  },


  {
    label: "Project",
    subMenu: [
      "New Project",
      { label: "Estimate", subMenu: ["New Estimate", "Smart AI Estimate"] },
      "Smart AI Estimate",
      { label: "Invoice", subMenu: ["New Invoice", "Add Payment"] },
      { label: "Job Site", subMenu: ["New Jobsite"] },
      { label: "Photo Gallery", subMenu: ["Upload"] },
    ],
  },
  {
    label: "Account",
    subMenu: [
      { label: "Subscription", subMenu: ["Make Payment"] },
      "My Donation",
      "Send Invite"
    ],
  },
  {
    label: "Company",
    subMenu: [
      { label: "Employee", subMenu: ["New", "Send Timecard Link", "Timecard Report"] },
    ],
  },
];

const routes: Record<string, string> = {
  "Customer": "/main/customer",
  "Customer/New Customer": "/main/customer/create",
  //"Customer/Create Project": "/main/project/create",
  //"Customer/View Project": "/main/project",
  "Customer/Payment History": "/main/customerpayment",
  "Customer/Documents Sent": "/main/customer/documents_sent",


  "Project": "/main/project",
  "Project/New Project": "/main/project/create",
  "Project/Estimate": "/main/project/estimate",
  "Project/Estimate/New Estimate": "/main/project/estimate/create",
  "Project/Estimate/Smart AI Estimate": "/main/project/estimate/smartassistant",
  "Project/Smart AI Estimate": "/main/project/estimate/smartassistant",
  "Project/Invoice": "/main/project/invoice",
  "Project/Invoice/New Invoice": "/main/project/invoice/create",
  "Project/Invoice/Add Payment": "/main/customerpayment/create",

  "Project/Job Site": "/main/project/jobsite",
  "Project/Job Site/New Jobsite": "/main/project/jobsite/create",
  "Project/Photo Gallery": "/main/project/photo",
  "Project/Photo Gallery/Upload": "/main/project/photo/upload",

  "Company": "/main/company",
  "Company/Edit": "/main/company/update",
  "Company/Employee": "/main/company/employee",
  "Company/Employee/New": "/main/company/employee/create",
  "Company/Employee/Send Timecard Link": "/main/company/employee/send_timecard",
  "Company/Employee/Timecard Report": "/main/company/employee/timecard_report",

  "Account": "/main/account",
  "Account/Subscription": "/main/account/subscription",
  "Account/Subscription/Make Payment": "/main/account/subscription/make_payment",
  "Account/Subscription/View Payment": "/main/account/subscription/view_payment",
  "Account/My Donation": "/main/account/donation",
  "Account/Send Invite": "/main/account/account_invite",
};

const HorizontalMenu: React.FC = () => {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine which main menu section is currently active based on the route
  const getActiveSection = (): string | null => {
    const path = location.pathname;

    // Check for Customer pages
    if (path.includes('/main/customer') || path === '/main/project/create') {
      return "Customer";
    }

    // Check for Project pages (except /main/project/create which belongs to Customer)
    if (path.includes('/main/project') && path !== '/main/project/create') {
      return "Project";
    }

    // Check for Account pages
    if (path.includes('/main/account')) {
      return "Account";
    }

    // Check for Company pages
    if (path.includes('/main/company')) {
      return "Company";
    }

    return null;
  };

  const activeSection = getActiveSection();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
        setActiveSubMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleNavigation = (menuLabel: string, subMenuItem?: string) => {
    const routeKey = subMenuItem ? `${menuLabel}/${subMenuItem}` : menuLabel;
    const path = routes[routeKey];
    //console.log("Navigating to:", path);
    if (path) {
      navigate(path);
      // Close menus after navigation
      setActiveMenu(null);
      setActiveSubMenu(null);
    }
  };

  const handleHoverMenu = (menuLabel: string) => {
    setActiveMenu(menuLabel);
    setActiveSubMenu(null);
  };

  const handleClickMenu = (e: React.MouseEvent, menuLabel: string, hasSubMenu: boolean) => {
    e.stopPropagation();
    if (hasSubMenu) {
      // If menu is not open, open it (for mobile). If already open, navigate.
      if (activeMenu !== menuLabel) {
        setActiveMenu(menuLabel);
        setActiveSubMenu(null);
      } else {
        handleNavigation(menuLabel);
      }
    } else {
      // No submenu, navigate directly
      handleNavigation(menuLabel);
    }
  };

  const handleHoverSubMenu = (subMenuLabel: string) => {
    setActiveSubMenu(subMenuLabel);
  };

  const renderSubMenu = (menuLabel: string, subMenu: SubMenuItem[]) => {
    return (
      <div className="absolute top-full left-0 bg-white shadow-lg rounded-b-lg border border-gray-200 border-t-0 min-w-[200px] z-50">
        {subMenu.map((subMenuItem) => {
          if (typeof subMenuItem === "string") {
            return (
              <div
                key={subMenuItem}
                className="px-4 py-2.5 cursor-pointer text-foreground hover:bg-gray-50 hover:text-primary transition-colors border-b border-gray-100 last:border-b-0"
                onClick={() => handleNavigation(menuLabel, subMenuItem)}
              >
                {subMenuItem}
              </div>
            );
          }

          const nestedPath = `${menuLabel}/${subMenuItem.label}`;
          const hasNestedSubMenu = !!subMenuItem.subMenu;

          return (
            <div
              key={subMenuItem.label}
              className="relative px-4 py-2.5 cursor-pointer text-foreground hover:bg-gray-50 hover:text-primary transition-colors border-b border-gray-100 last:border-b-0"
              onMouseEnter={() => handleHoverSubMenu(nestedPath)}
              onMouseLeave={() => setActiveSubMenu(null)}
              onClick={(e) => {
                e.stopPropagation();
                // If has nested submenu and it's not open yet, open it (for mobile)
                // Otherwise, navigate
                if (hasNestedSubMenu && activeSubMenu !== nestedPath) {
                  setActiveSubMenu(nestedPath);
                } else {
                  handleNavigation(menuLabel, subMenuItem.label);
                }
              }}
            >
              <div className="flex items-center justify-between">
                <span>{subMenuItem.label}</span>
                {hasNestedSubMenu && <span className="text-gray-400 ml-2">›</span>}
              </div>
              {activeSubMenu === nestedPath && subMenuItem.subMenu && (
                <div className="absolute top-0 left-full ml-0 bg-white shadow-lg rounded-lg border border-gray-200 min-w-[180px] z-50">
                  {subMenuItem.subMenu.map((nestedSubMenuItem) => (
                    <div
                      key={nestedSubMenuItem}
                      className="px-4 py-2.5 cursor-pointer text-foreground hover:bg-gray-50 hover:text-primary transition-colors border-b border-gray-100 last:border-b-0 last:rounded-b-lg"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigation(`${menuLabel}/${subMenuItem.label}`, nestedSubMenuItem);
                      }}
                    >
                      {nestedSubMenuItem}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      ref={menuRef}
      className="bg-page-accent-soft flex justify-center items-center w-full bg-gray-50 border-b border-gray-200 py-2"
    >
      {menuItems.map((menuItem) => {
        const isActive = activeSection === menuItem.label;
        const hasSubMenu = !!menuItem.subMenu;

        return (
          <div
            key={menuItem.label}
            className="relative mx-0.5 sm:mx-2"
            onMouseEnter={() => handleHoverMenu(menuItem.label)}
            onMouseLeave={() => setActiveMenu(null)}
          >
            <button
              onClick={(e) => handleClickMenu(e, menuItem.label, hasSubMenu)}
              className={`px-2 sm:px-4 py-2 font-semibold transition-colors rounded-t-lg text-sm sm:text-base ${isActive
                  ? 'text-primary' // Active section: always orange
                  : 'text-foreground hover:text-primary' // Inactive: black, orange on hover
                } ${activeMenu === menuItem.label ? 'bg-white border-x border-t border-gray-200' : ''
                }`}
            >
              {menuItem.label}
            </button>
            {activeMenu === menuItem.label && menuItem.subMenu && renderSubMenu(menuItem.label, menuItem.subMenu)}
          </div>
        );
      })}
    </div>
  );
};

export default HorizontalMenu;